import React, { useEffect, useState } from 'react';
import { ForgeIcon, ForgeScaffold } from '@tylertech/forge-react';
import EmailStep from './EmailStep.js';
import EmailMismatchStep from './EmailMismatchStep.js';
import LoginButtons from './LoginButtons.js';
import { LOGIN_ERROR_MESSAGE } from './loginHelpers.js';
import './LoginPage.scss';
import { useUser } from '../../contexts/userContext.js';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router';
import { buildPath } from '../../services/navigateService.js';
import { getLoginConfiguration, login } from '../../services/ssoService.js';
import { SSO_PROVIDER_TYLER } from '../../static/keyWords.js';
import { REGEX_VALIDATIONS } from '../../static/constants.js';

const BYPASS_EMAIL_VALIDATION_ENVIRONMENTS = new Set(['local', 'dev']);
const LOGIN_FEATURES = [
  {
    icon: 'target',
    title: 'Align Priorities',
    description: 'Connect programs to outcomes that matter.',
  },
  {
    icon: 'insert_chart_outlined',
    title: 'Make Informed Decisions',
    description: 'Use data and scenarios to drive better budget choices.',
  },
  {
    icon: 'groups',
    title: 'Engage Your Community',
    description: 'Increase transparency and build trust with stakeholders.',
  },
  {
    icon: 'verified_user',
    title: 'Maximize Impact',
    description: 'Invest in programs that deliver the greatest value.',
  },
];

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [loginConfiguration, setLoginConfiguration] = useState({
    multipleSsoEnabled: false,
    defaultProvider: SSO_PROVIDER_TYLER,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [emailInvalid, setEmailInvalid] = useState(false);
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isEmailMismatch = params.get('ssoEmailMismatch') === 'true';

  useEffect(() => {
    if (user && !isEmailMismatch) {
      navigate(buildPath(user), { replace: true });
      return;
    }

    if (isEmailMismatch) {
      return;
    }

    const loadLoginConfiguration = async () => {
      try {
        const configuration = await getLoginConfiguration();
        setLoginConfiguration((currentConfiguration) => ({
          ...currentConfiguration,
          ...configuration,
        }));
      } catch {
        // Tyler remains the default during a rolling backend deployment.
      }
    };

    loadLoginConfiguration();
  }, [isEmailMismatch, navigate, user]);

  const isValidEmail = (value) => REGEX_VALIDATIONS.email.test(value.trim());

  const handleEmailChange = (value) => {
    setEmail(value);
    setEmailInvalid(false);
    setErrorMessage('');
  };

  const startAuthentication = async (
    ssoProvider = loginConfiguration.defaultProvider
  ) => {
    if (isSubmitting) {
      return;
    }

    const bypassEmailValidation = BYPASS_EMAIL_VALIDATION_ENVIRONMENTS.has(
      process.env.ENVIRONMENT
    );

    if (!bypassEmailValidation && !isValidEmail(email)) {
      setEmailInvalid(true);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      await login(email.trim(), ssoProvider);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message
          ?? LOGIN_ERROR_MESSAGE
      );
      setIsSubmitting(false);
    }
  };

  if (isEmailMismatch) {
    return <EmailMismatchStep />;
  }

  return (
    <ForgeScaffold className="loginScaffold">
      <main slot="body" className="loginPage">
        <section className="loginStory" aria-label="About Priority Based Budgeting">
          <div className="loginStoryContent">
            <div className="tylerBrand">
              <img
                src="https://cdn.forge.tylertech.com/v1/images/branding/tyler/tyler-logo-color.svg"
                alt="Tyler Technologies"
              />
            </div>

            <div className="loginStoryIntro">
              <h1>Smarter budgets.<br />Stronger communities.</h1>
              <span className="storyAccent" aria-hidden="true" />
              <p>
                <span className="loginStoryProductName">Priority Based Budgeting</span>
                {' '}(PBB) helps public sector leaders align resources with what
                matters most.
              </p>
            </div>

            <div className="loginFeatures">
              {LOGIN_FEATURES.map((feature) => (
                <div className="loginFeature" key={feature.title}>
                  <span className="featureIcon">
                    <ForgeIcon external externalType="all" name={feature.icon} />
                  </span>
                  <span>
                    <strong>{feature.title}</strong>
                    <small>{feature.description}</small>
                  </span>
                </div>
              ))}
            </div>

            <small className="loginCopyright">
              © {new Date().getFullYear()} Tyler Technologies, Inc. All rights reserved.
            </small>
          </div>
        </section>

        <section className="loginAccess" aria-label="Sign in">
          <form
            className="loginCard"
            onSubmit={(event) => {
              event.preventDefault();
              startAuthentication(loginConfiguration.defaultProvider);
            }}
            noValidate
          >
            <div className="productMark" aria-hidden="true">
              <ForgeIcon external externalType="all" name="account_balance" />
            </div>

            <div className="productTitle">
              <h2>Priority Based Budgeting</h2>
              <div className="pbbDivider">
                <span />
                <strong>PBB</strong>
                <span />
              </div>
              <p>Sign in to access your PBB workspace</p>
            </div>

            <EmailStep
              email={email}
              setEmail={handleEmailChange}
              invalid={emailInvalid}
              disabled={isSubmitting}
            />
            <LoginButtons
              onLogin={startAuthentication}
              multipleSsoEnabled={loginConfiguration.multipleSsoEnabled}
              defaultProvider={loginConfiguration.defaultProvider}
              disabled={isSubmitting}
            />
            {errorMessage ? (
              <p className="loginError" role="alert">{errorMessage}</p>
            ) : null}
          </form>
        </section>
      </main>
    </ForgeScaffold>
  );
};

export default LoginPage;
